import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import arrowLeft from "../../Assets/icon/arrow-narrow-left.svg";
import { useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import right from "../../Assets/images/chevron-right.svg";
import faithIcon from "../../Assets/icon/prey.svg";
import voiceIcon from "../../Assets/icon/voice.svg";
import { Play, Pause, Edit } from "lucide-react";
import "./style.css";
import GenericModal from "../../Components/Modal/GenericModal";
import FaithBeliefSettings from "./FaithBeliefSettings";
import VoicePreference from "./VoicePreference";
import { getUserFaith, updateUserFaith } from "../../Services/subscription";
import { getAudioFile, getVoiceOptions, getVoiceOptionByValueOrId } from "../../Utils/CommonFunctions/voiceMapping";
import { ReactComponent as SmallScreenLeftArrow } from "../../Assets/icon/arrow-narrow-left-white.svg";
import { ReactComponent as LeftArrow } from "../../Assets/icon/arrow-narrow-left.svg";
import { ReactComponent as EditIcon } from "../../Assets/images/edit.svg";

import "../../Pages/TermsofUse/style.css";

// `forcedSection` ("voice" | "faith") renders a single section directly as a
// standalone page (used by the top-level Voice Preference / Faith &
// Spirituality menu entries). When unset, the normal Settings hub is shown.
function Settings({ forcedSection = null }) {
  const navigate = useNavigate();
  const sectionTitles = {
    voice: "Voice Preference",
    faith: "Faith & Spirituality",
  };
  const pageTitle = forcedSection ? sectionTitles[forcedSection] : "Settings";
  const {
    setHeaderContent,
    profileContent,
    addProfileData,
    setbeliefSystem,
  } = useHeader();

  useEffect(() => {
    setHeaderContent(false);
  }, [setHeaderContent]);

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [activeSection, setActiveSection] = useState(forcedSection || "main");
  const [isFaithModalOpen, setIsFaithModalOpen] = useState(false);
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const [faithData, setFaithData] = useState({ faith: "", toggle_state: false, status: "" });
  const [faithLoading, setFaithLoading] = useState(true);
  const [faithError, setFaithError] = useState("");
  const [isFaithToggleUpdating, setIsFaithToggleUpdating] = useState(false);
  const [isVoicePreviewPlaying, setIsVoicePreviewPlaying] = useState(false);
  const voiceAudioRef = useRef(null);
  const faithFormRef = useRef(null);
  const [faithSaving, setFaithSaving] = useState(false);
  const voiceFormRef = useRef(null);
  const [voiceSaving, setVoiceSaving] = useState(false);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadFaithData = useCallback(async () => {
    setFaithLoading(true);
    setFaithError("");
    try {
      const response = await getUserFaith();
      const payload = response?.payload ?? response;
      setFaithData({
        faith: payload?.faith || "",
        toggle_state: Boolean(payload?.toggle_state),
        status: payload?.status || payload?.subscription_status || "",
      });
      if (typeof setbeliefSystem === "function") {
        setbeliefSystem(payload || {});
      }
    } catch (error) {
      setFaithError("Unable to load faith settings. Please try again.");
    } finally {
      setFaithLoading(false);
    }
  }, [setbeliefSystem]);

  useEffect(() => {
    loadFaithData();
  }, [loadFaithData]);

  useEffect(() => {
    return () => {
      try {
        voiceAudioRef.current?.pause();
      } catch (_) { }
    };
  }, []);

  const settingsMenu = [
    { id: 1, icon: faithIcon, text: "Faith & Spirituality" },
    { id: 2, icon: voiceIcon, text: "Voice Preference" },
  ];

  const onSettingClick = (id) => {
    if (id === 1) setActiveSection("faith");
    else if (id === 2) setActiveSection("voice");
  };

  const voiceOptions = useMemo(() => getVoiceOptions(), []);
  const selectedVoiceValue = profileContent?.aris_voice || "";
  // Use getVoiceOptionByValueOrId to handle both value (e.g., "Female") and openAIVoice ID (e.g., "echo")
  const selectedVoiceOption = useMemo(
    () => getVoiceOptionByValueOrId(selectedVoiceValue),
    [selectedVoiceValue],
  );

  const handleVoicePreview = useCallback(() => {
    const audio = voiceAudioRef.current;
    if (!audio || !selectedVoiceValue) return;

    if (isVoicePreviewPlaying) {
      audio.pause();
      audio.currentTime = 0;
      setIsVoicePreviewPlaying(false);
      return;
    }

    audio.currentTime = 0;
    audio
      .play()
      .then(() => setIsVoicePreviewPlaying(true))
      .catch(() => setIsVoicePreviewPlaying(false));
  }, [isVoicePreviewPlaying, selectedVoiceValue]);

  const handleFaithToggleChange = async (event) => {
    const enabled = event.target.checked;
    if (isFaithToggleUpdating) return;

    setIsFaithToggleUpdating(true);
    setFaithError("");
    setFaithData((prev) => ({ ...prev, toggle_state: enabled }));
    try {
      await updateUserFaith({
        faith: faithData?.faith || "",
        toggle_state: enabled,
      });
      await loadFaithData();
    } catch (error) {
      setFaithError(
        error?.response?.data?.message || error?.message || "Unable to update faith settings.",
      );
      await loadFaithData();
    } finally {
      setIsFaithToggleUpdating(false);
    }
  };

  const handleFaithModalClose = () => {
    setIsFaithModalOpen(false);
    loadFaithData();
  };

  const handleVoiceModalClose = () => {
    setIsVoiceModalOpen(false);
    if (typeof addProfileData === "function") {
      addProfileData();
    }
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current.currentTime = 0;
    }
    setIsVoicePreviewPlaying(false);
  };

  const backToMain = () => {
    setActiveSection("main");
    setFaithError("");
    setIsVoicePreviewPlaying(false);
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current.currentTime = 0;
    }
  };

  const handleHeaderBack = () => {
    // In forced single-section mode there is no "main" hub to return to.
    if (!forcedSection && activeSection !== "main") {
      backToMain();
      return;
    }
    navigate("/dashboard");
  };



  return (
    <div className="settings-container">
      <header className="termsofuse-header">
        <div className="termsofuse-gradient-background ">
          <div className="termsofuse-nav-container">
            <div
              className="termsofuse-logo-container "
              onClick={handleHeaderBack}
            >
              {screenWidth < 480 && (
                <SmallScreenLeftArrow className="termsofuse-back-button" />
              )}
              {screenWidth >= 480 && (
                <LeftArrow className="termsofuse-back-button" />
              )}
            </div>
            <h1 className="head-not-txt">{pageTitle}</h1>
            <div className="termsofuse-space"></div>
          </div>
        </div>
      </header>

      <div className={`settings-hero-container ${activeSection !== "main" ? "settings-detail-mode" : ""}`}>
        {activeSection === "main" && (
          <div className="set-acc-container">
            {settingsMenu?.map((_menu) => (
              <div
                className="set-accordian-menu"
                key={_menu?.id}
                onClick={() => onSettingClick(_menu?.id)}
              >
                <div className="set-left-section">
                  <img src={_menu?.icon} alt={_menu?.text} />
                  <div className="set-name">{_menu?.text}</div>
                </div>
                <div className="set-right-menu">
                  <img src={right} alt="right" />
                </div>
              </div>
            ))}
          </div>
        )}

        {activeSection === "faith" && (
          <div className="settings-detail-wrapper">
            <h2 className="settings-detail-title">Faith &amp; Spirituality</h2>

            {faithError && (
              <div className="settings-error-banner">{faithError}</div>
            )}

            <div className="settings-detail-card">
              {faithLoading ? (
                <div className="settings-card-loading">Loading faith settings…</div>
              ) : (
                <div className="settings-card">
                  <div className="settings-card-left">
                    <div className="settings-card-header">
                      <div className="settings-card-heading">Integrate Your Faith and Belief System</div>
                      <div className="settings-card-subtitle">
                        Premium option for personalized, faith-aligned coaching
                      </div>
                    </div>
                    <div className="settings-card-body">
                      <div className="settings-card-label">Your faith and belief system:</div>
                      <div className="settings-card-selection">
                        <span className={`settings-card-selection-value ${faithData?.faith ? "" : "muted"}`}>
                          {faithData?.faith || "Not Selected"}
                        </span>
                        <button
                          type="button"
                          className="settings-inline-edit"
                          onClick={() => setIsFaithModalOpen(true)}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="settings-card-right">
                    <label className={`settings-toggle ${isFaithToggleUpdating ? "disabled" : ""}`}>
                      <input
                        type="checkbox"
                        checked={Boolean(faithData?.toggle_state)}
                        onChange={handleFaithToggleChange}
                        disabled={isFaithToggleUpdating}
                      />
                      <span className="settings-toggle-slider" />
                    </label>

                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === "voice" && (
          <div className="settings-detail-wrapper">
            <h2 className="settings-detail-title">Voice Preference</h2>

            <div className="settings-detail-card">
              <div className="settings-voice-content">
                <div className="settings-voice-info">
                  <div className="settings-voice-icon">
                    <img src={voiceIcon} alt="voice" />
                  </div>
                  <div className="settings-voice-name">
                    {selectedVoiceOption?.label || "Not Selected"}
                    <button
                      type="button"
                      className="settings-voice-preview"
                      onClick={handleVoicePreview}
                      disabled={!selectedVoiceValue}
                    >
                      {isVoicePreviewPlaying ? <Pause size={18} /> : <Play size={18} />}
                    </button>
                  </div>
                </div>

                <div className="settings-voice-actions">
                  <button
                    type="button"
                    className="settings-inline-edit"
                    onClick={() => setIsVoiceModalOpen(true)}
                  >
                    <EditIcon style={{ marginRight: "8px" }} />
                    Edit
                  </button>
                </div>
              </div>
              <audio
                ref={voiceAudioRef}
                src={selectedVoiceValue ? getAudioFile(selectedVoiceValue) : ""}
                onEnded={() => setIsVoicePreviewPlaying(false)}
              />
            </div>
          </div>
        )}
        <GenericModal
          isOpen={isFaithModalOpen}
          onClose={handleFaithModalClose}
          heading="Integrate Your Faith and Belief System"
          primaryLabel="Save"
          secondaryLabel="Cancel"
          onPrimary={() => faithFormRef.current?.save?.()}
          onSecondary={handleFaithModalClose}
          loading={faithSaving}
        >
          <FaithBeliefSettings ref={faithFormRef} onClose={handleFaithModalClose} onLoadingChange={setFaithSaving} />
        </GenericModal>

        <GenericModal
          isOpen={isVoiceModalOpen}
          onClose={handleVoiceModalClose}
          heading="Voice Preference"
          primaryLabel="Update"
          secondaryLabel="Cancel"
          maxWidth="548px"
          onPrimary={() => voiceFormRef.current?.save?.()}
          onSecondary={handleVoiceModalClose}
          loading={voiceSaving}
        >
          <VoicePreference ref={voiceFormRef} onClose={handleVoiceModalClose} onLoadingChange={setVoiceSaving} />
        </GenericModal>
      </div>
    </div>
  );
}

export default Settings;
